import AudioRecorder from './AudioRecorder';
import AudioPlayer from './AudioPlayer';
import { AudioConfig, type AudioConfigOptions } from './AudioConfig';
import pako from 'pako';
import FileSaver from './FileSaver';

/**
 * 对话管理器类，负责管理WebSocket连接、音频录制和相关事件处理
 */
export default class ChatManager extends EventTarget {
  // 事件类型常量
  static readonly ASR_EVENT = 'asrEvent';
  static readonly SUBTITLE_EVENT = 'subtitleEvent';
  static readonly BLEND_SHAPE_EVENT = 'blendShapeEvent';
  static readonly PLAY_END_EVENT = 'playEndEvent';
  static readonly POWER_EVENT = 'powerEvent';
  static readonly DIS_CONNECT_EVENT = 'disConnectEvent';
  static readonly CLIENTS_FULL_EVENT = 'clientsFullEvent';

  private chatSocket: WebSocket | null = null;
  private readonly chatServerUrl: string;
  private isChatConnected: boolean = false;
  private readonly audioRecorder: AudioRecorder;
  public readonly audioPlayer: AudioPlayer;
  private readonly audioConfig: AudioConfig;
  private readonly fileSaver: FileSaver;

  /**
   * 是否启用数据保存功能 (保存音频和BS数据到本地JSON文件)
   */
  public enableA2ESaving: boolean = false;

  constructor(serverUrl: string, audioOptions?: AudioConfigOptions) {
    super();

    this.chatServerUrl = serverUrl;
    this.audioConfig = new AudioConfig(audioOptions);
    this.fileSaver = new FileSaver();

    // 将配置传递给AudioRecorder和AudioPlayer
    this.audioRecorder = new AudioRecorder(this.audioConfig);
    this.audioPlayer = new AudioPlayer(this.audioConfig);

    this.audioRecorder.addEventListener(AudioRecorder.FRAME_EVENT, (event: Event) => {
      const base64Pcm = (event as CustomEvent).detail.data;
      const is_final = (event as CustomEvent).detail.is_final;

      if (base64Pcm) {
        // console.log(`frameData:${typeof base64Pcm} ${base64Pcm.length} `);
        this.sendAudioData(base64Pcm, is_final);
      }
    });

    // 监听功率事件并重新分发
    this.audioRecorder.addEventListener(AudioRecorder.POWER_EVENT, (event: Event) => {
      this.dispatchEvent(
        new CustomEvent(ChatManager.POWER_EVENT, {
          detail: (event as CustomEvent).detail,
        })
      );
    });

    // 监听 AudioPlayer 的 BLEND_SHAPE_EVENT 事件并重新分发
    this.audioPlayer.addEventListener(AudioPlayer.BLEND_SHAPE_EVENT, (event: Event) => {
      this.dispatchEvent(
        new CustomEvent(ChatManager.BLEND_SHAPE_EVENT, {
          detail: (event as CustomEvent).detail,
        })
      );
    });

    // 监听 AudioPlayer 的 PLAY_END_EVENT 事件 延迟2秒发送
    this.audioPlayer.addEventListener(AudioPlayer.PLAY_END_EVENT, () => {
      setTimeout(() => {
        if (!this.audioPlayer.playing) this.dispatchEvent(new CustomEvent(ChatManager.PLAY_END_EVENT));
      }, 2000);
    });

  }

  /**
   * 连接音频服务器
   * @param uid 可选的用户标识
   * @param voice 可选的音色标识
   * @param llm 可选的LLM模型标识
   * @returns Promise<void> 连接建立完成的Promise
   */
  public connect(uid?: string, voice?: string, llm?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.chatSocket?.readyState === WebSocket.CONNECTING) {
        console.log('连接正在建立中...');
        reject(new Error('连接正在建立中'));
        return;
      }

      try {
        console.log(`连接对话服务器: ${this.chatServerUrl}`);
        this.chatSocket = new WebSocket(this.chatServerUrl);
        this.chatSocket.binaryType = 'arraybuffer';

        // 直接在这里设置事件处理，但保持逻辑清晰
        this.chatSocket.onopen = () => {
          console.log('对话服务器已连接');
          this.isChatConnected = true;

          // 发送身份标识信息
          this.sendIdentityIfNeeded(uid, voice, llm);
          resolve();
        };

        this.chatSocket.onmessage = (event) => {
          this.handleServerMessage(event.data);
        };

        this.chatSocket.onclose = (event) => {
          console.log(`对话服务器连接关闭: ${event.code} - ${event.reason}`);
          this.isChatConnected = false;
          this.dispatchEvent(new CustomEvent(ChatManager.DIS_CONNECT_EVENT));
        };

        this.chatSocket.onerror = (error) => {
          console.error('对话服务器连接错误:', error);
          alert('服务器连接错误 先访问 ' + this.chatServerUrl + ' 查看原因');
          this.isChatConnected = false;
          reject(error);
        };
      } catch (error) {
        console.error('创建WebSocket连接失败:', error);
        reject(error);
      }
    });
  }

  /**
   * 发送身份标识信息（如果需要）
   */
  private sendIdentityIfNeeded(uid?: string, voice?: string, llm?: string): void {
    if (uid || voice || llm) {
      const identityData = {
        type: 'identity',
        uid: uid,
        voice: voice,
        llm: llm,
      };
      this.chatSocket?.send(JSON.stringify(identityData));
      // console.log('已发送身份标识信息:', identityData);
    }
  }

  /**
   * 处理服务器消息
   */
  private handleServerMessage(data: string | ArrayBuffer): void {
    // console.log(typeof data, data instanceof ArrayBuffer ? data.byteLength : data.length);
    try {
      let jsonString: string;
      if (typeof data === 'string') {
        jsonString = data;
      } else if (data instanceof ArrayBuffer) {
        try {
          const decompressed = pako.inflate(new Uint8Array(data));
          jsonString = new TextDecoder().decode(decompressed);
        } catch (err) {
          console.error('解压失败:', err);
          return;
        }
      } else {
        console.warn('收到未知类型的数据:', data);
        return;
      }


      const json = JSON.parse(jsonString);
      // console.log('Received Chat Server:', json);

      if (json.type === 'clients_full') {
        // console.log('Server: Clients Full');
        this.dispatchEvent(new CustomEvent(ChatManager.CLIENTS_FULL_EVENT));
      }

      if (json.type === 'sub') {
        // console.log('subtitle:', json);
        this.dispatchEvent(
          new CustomEvent(ChatManager.SUBTITLE_EVENT, {
            detail: { sub: json.sub, is_first_sentence: json.is_first_sentence },
          })
        );
      }

      if (json.type === 'asr') {
        // console.log('ASR:', json);
        if (this.audioPlayer.playing) {
          console.log('ASR 打断播放');
          this.audioPlayer.stop();
        }
        this.dispatchEvent(
          new CustomEvent(ChatManager.ASR_EVENT, {
            detail: { asr: json.asr },
          })
        );
      }

      if (json.type === 'a2e') {
        // 播放音频数据
        if (json.audio) {
          // console.log('Received A2E enableA2ESaving:', this.enableA2ESaving);
          if (this.enableA2ESaving) {
            this.fileSaver.start();
            this.fileSaver.write({ audio: json.audio, bs: json.bs });
          }

          this.audioPlayer.addAudio(json.audio, json.bs);
        }
      }
    } catch (error) {
      console.error('解析服务器消息失败:', error);
    }
  }

  /**
   * 发送语音对话音频数据到服务器
   */
  private sendAudioData(data: string, is_final: boolean): void {
    // if (this.audioPlayer.playing && is_final) {
    //   console.log('新对话音频 打断播放');
    //   this.audioPlayer.stop();
    // }
    if (this.chatSocket?.readyState === WebSocket.OPEN) {
      try {
        const audioData = {
          type: 'audio',
          audio: data,
          is_final: is_final,
        };
        this.chatSocket?.send(JSON.stringify(audioData));
      } catch (error) {
        console.error('发送音频数据失败:', error);
      }
    } else {
      console.warn('对话服务器未连接，无法发送数据');
    }
  }

  /**
   * 发送对话文本到服务器
   */
  public sendTextData(data: string): void {
    if (this.audioPlayer.playing) {
      console.log('新对话文本 打断播放');
      this.audioPlayer.stop();
    }
    if (this.chatSocket?.readyState === WebSocket.OPEN) {
      try {
        const textData = {
          type: 'text',
          text: data,
        };
        this.chatSocket?.send(JSON.stringify(textData));
      } catch (error) {
        console.error('发送文本数据失败:', error);
      }
    } else {
      console.warn('对话服务器未连接，无法发送数据');
    }
  }


  /**
   * 发送无脑TTS文本到服务器
   */
  public sendTtsTextData(data: string): void {
    if (this.audioPlayer.playing) {
      console.log('新TTS文本 打断播放');
      this.audioPlayer.stop();
    }
    if (this.chatSocket?.readyState === WebSocket.OPEN) {
      try {
        const textData = {
          type: 'tts_text',
          text: data,
        };
        this.chatSocket?.send(JSON.stringify(textData));
      } catch (error) {
        console.error('发送TTS文本数据失败:', error);
      }
    } else {
      console.warn('对话服务器未连接，无法发送数据');
    }
  }

  /**
   * 重置对话服务器
   */
  public resetChatServer(): void {
    if (this.chatSocket?.readyState === WebSocket.OPEN) {
      const _data = {
        type: 'reset',
      };
      this.chatSocket?.send(JSON.stringify(_data));
      console.log('已发送重置命令');
    } else {
      console.warn('对话服务器未连接，无法重置');
    }
  }

  /**
   * 断开对话服务器
   */
  public disconnect(): void {
    console.log('断开对话服务器');

    // 停止录音
    if (this.audioRecorder.recording) {
      this.audioRecorder.stopRecording().catch(console.error);
    }

    this.audioPlayer.stop();
    this.fileSaver.close();

    // 关闭WebSocket连接
    if (this.chatSocket) {
      this.chatSocket.close(1000, '正常关闭');
      this.chatSocket = null;
    }
    this.isChatConnected = false;
  }

  /**
   * 开始麦克风录音
   */
  public async startMic(): Promise<AudioRecorder> {
    try {
      return await this.audioRecorder.startRecording();
    } catch (error) {
      console.error('开始录音失败:', error);
      throw error;
    }
  }

  /**
   * 停止麦克风录音
   */
  public async stopMic(): Promise<AudioRecorder> {
    try {
      return await this.audioRecorder.stopRecording();
    } catch (error) {
      console.error('停止录音失败:', error);
      throw error;
    }
  }

  /**
   * 获取音频配置
   */
  public getAudioConfig(): AudioConfig {
    return this.audioConfig;
  }

  /**
   * 获取连接状态
   */
  public get isConnected(): boolean {
    return this.isChatConnected;
  }

  /**
   * 获取录音状态
   */
  public get isRecording(): boolean {
    return this.audioRecorder.recording;
  }


  /**
   * 播放本地JSON文件中的音频和BlendShape数据
   * @param url JSON文件地址
   */
  public async playA2EJson(url: string): Promise<void> {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (!Array.isArray(data)) throw new Error('Invalid JSON format: expected an array');

      this.audioPlayer.stop(); // 停止当前播放

      // 遍历数据并添加到播放队列
      data.forEach((item, index) => {
        if (item?.audio) {
          this.audioPlayer.addAudio(item.audio, item.bs);
        }
      });

      console.log(`已加载并开始播放本地JSON: ${url}`);
    } catch (error) {
      console.error('播放本地JSON失败:', error);
    }
  }


  /**
   * 保存A2E数据
   */
  public async saveA2EJson(): Promise<void> {
    try {
      if (!this.enableA2ESaving) return;
      this.fileSaver.save();
    } catch (error) {
      console.error('保存A2E失败:', error);
    }
  }


  /**
   * 销毁资源
   */
  public dispose(): void {
    console.log('销毁 ChatManager');

    // 销毁录音器
    this.audioRecorder.dispose();

    // 销毁音频播放器
    this.audioPlayer.dispose();
    this.fileSaver.close();

    // 关闭WebSocket连接
    if (this.chatSocket) {
      this.chatSocket.close(1000, '正常关闭');
      this.chatSocket = null;
    }

    this.isChatConnected = false;
  }
}
